/**
 * PRINT.1 (spec 3.8): the only place the blank wizards turn a user choice
 * (specialty product type + sticker type, or the manual "Product kind" chip)
 * into print-core production fields. Pure, no React.
 */
import {
  PRESET_KEYS,
  PRODUCTION_PRESETS,
  createPrintSpec,
  productionFromPreset,
  parseSizeInches,
  sizeKey,
} from "print-core";

/** Specialty wizard: stickerType -> preset. */
const STICKER_PRESETS = {
  dieCut: "dieCutSticker",
  kissCut: "kissCutSticker",
  stickerSheet: "stickerSheet",
  rollLabel: "rollLabel",
};

/** Specialty wizard: productType (non sticker) -> preset. */
const TYPE_PRESETS = {
  poster: "poster",
  banner: "poster",
  nester: "dtfGangSheet",
  postcard: "postcard",
  // Flat print paper products. print-core carries their own bleed and safe
  // defaults (business card and flyer safe 0.125, poster safe 0.25).
  businessCard: "businessCard",
  flyer: "flyer",
  greetingCard: "greetingCard",
};

/** Sheet key for the single DTF gang sheet spec in settings.printSpecs. */
export const DTF_SHEET_KEY = "sheet";

/**
 * Resolve the production preset key for a wizard state.
 *   - is3DProduct always wins (the manual 3D checkbox).
 *   - productType "stickers" + stickerType -> sticker presets.
 *   - productType poster | banner | nester | postcard -> their preset.
 *   - productKind (manual wizard chip) when it is a known preset.
 *   - otherwise "apparel".
 */
export function presetFor({
  productType,
  stickerType,
  is3DProduct,
  productKind,
} = {}) {
  if (is3DProduct) return "object3d";
  if (productType === "stickers" || (!productType && STICKER_PRESETS[stickerType])) {
    return STICKER_PRESETS[stickerType] || "dieCutSticker";
  }
  if (TYPE_PRESETS[productType]) return TYPE_PRESETS[productType];
  if (productKind && PRESET_KEYS.includes(productKind)) return productKind;
  return "apparel";
}

/**
 * Catalog masters already have a merchandising / production kind. Use that
 * instead of asking again on the manual wizard when we adopt a master.
 */
export function productKindFromMaster(master = {}) {
  if (!master || typeof master !== "object") return "apparel";
  if (master.printPreset && PRESET_KEYS.includes(master.printPreset)) {
    return master.printPreset;
  }
  const fromFields = presetFor({
    productType: master.productType || master.type,
    stickerType: master.stickerType,
    productKind: master.productKind,
  });
  if (fromFields && fromFields !== "apparel") return fromFields;
  const ghost = String(master.ghostCategoryId || "").toLowerCase();
  const group = `${master.group || ""} ${master.categoryLabel || ""}`.toLowerCase();
  if (ghost === "sticker" || group.includes("sticker")) return "dieCutSticker";
  return "apparel";
}

/** `{ production, printPreset }` to spread into blank settings on save. */
export function settingsPatchFor(preset) {
  const { production } = productionFromPreset(preset);
  return { production, printPreset: preset };
}

/** Chip options for the manual wizard "Product kind" row (3D is a checkbox). */
export const PRODUCT_KIND_OPTIONS = PRESET_KEYS.filter(
  (key) => key !== "object3d"
).map((key) => ({ id: key, label: PRODUCTION_PRESETS[key].label }));

/** Kinds that use the per size shape setup instead of the mockup rectangle. */
export function isShapeKind(preset) {
  return preset !== "apparel" && preset !== "object3d";
}

/**
 * Map stored production fields back to the legacy stickerType string for
 * callers that still branch on it. Never reads a name.
 */
export function stickerTypeFromProduction(production) {
  if (!production || production.category !== "adhesive") return null;
  if (production.cut === "dieCut") return "dieCut";
  if (production.cut === "kissCut") {
    return production.layout === "nested" ? "stickerSheet" : "kissCut";
  }
  if (production.cut === "rollCut") return "rollLabel";
  return null;
}

/** print-core size key for a variant size string, or null when unreadable. */
export function sizeKeyFor(size) {
  const parsed = parseSizeInches(size);
  return parsed ? sizeKey(parsed.width, parsed.height) : null;
}

/** Keys for a list of size strings or `{ size }` rows, unreadable ones dropped. */
export function sizeKeysFor(sizes = []) {
  const keys = [];
  (sizes || []).forEach((entry) => {
    const key = sizeKeyFor(typeof entry === "string" ? entry : entry?.size);
    if (key && !keys.includes(key)) keys.push(key);
  });
  return keys;
}

/** True when every size has a PrintSpec. */
export function allSizesHaveSpecs(sizes, printSpecs) {
  const keys = sizeKeysFor(sizes);
  if (!keys.length) return false;
  return keys.every((key) => !!printSpecs?.[key]);
}

/**
 * Alert state for the manual wizard's Sizes-and-shape step. Returns:
 * - "noSizes": no sizes in the Variants step yet
 * - "noParseableSizes": sizes exist but none can be read as inches
 * - "pickShape": parseable sizes exist but at least one has no spec
 * - null: every parseable size has a spec, no alert needed
 */
export function sizesShapeAlertKey(sizes, printSpecs) {
  if (!sizes || !sizes.length) return "noSizes";
  const keys = sizeKeysFor(sizes);
  if (!keys.length) return "noParseableSizes";
  if (keys.every((key) => !!printSpecs?.[key])) return null;
  return "pickShape";
}

/** Keep only the specs for the sizes that still exist. */
export function pruneSpecsToSizes(sizes, printSpecs) {
  const keys = sizeKeysFor(sizes);
  const out = {};
  keys.forEach((key) => {
    if (printSpecs?.[key]) out[key] = printSpecs[key];
  });
  return out;
}

/**
 * DTF gang sheet: the nested PrintSpec is built from the sheet dimensions.
 * Only the edge margin (safe) is editable; bleed stays 0.
 */
export function buildDtfSheetSpec({ width, maxHeight, safe, existing } = {}) {
  const w = Number(width);
  const h = Number(maxHeight);
  if (!(w > 0) || !(h > 0)) return null;
  const { production } = productionFromPreset("dtfGangSheet");
  const margin = Number.isFinite(Number(safe)) && safe !== "" && safe != null
    ? Math.max(0, Number(safe))
    : existing?.safe ?? 0.1;
  return createPrintSpec({
    id: existing?.id,
    version: existing?.version,
    name: existing?.name || "DTF gang sheet",
    production,
    dieline: { source: "rect", params: { width: w, height: h } },
    bleed: 0,
    safe: margin,
    layout: {
      kind: "nested",
      gap: 0.1,
      rotation: "90",
      length: { mode: "auto", max: h },
    },
  });
}
